const ACCESS_TOKEN_KEY = 'missionfund.accessToken';
const ACCESS_TOKEN_EXPIRES_AT_KEY = 'missionfund.accessTokenExpiresAt';

export const ACCESS_TOKEN_SESSION_TIMEOUT_MS = 60 * 60 * 1000;
export const ACCESS_TOKEN_STORAGE_EVENT = 'missionfund:access-token-changed';

const emitTokenStorageChange = () => {
  try {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(ACCESS_TOKEN_STORAGE_EVENT));
  } catch {
    // ignore event dispatch failures
  }
};

const clearStoredToken = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
};

const readExpiryAt = (): number | null => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const getAccessTokenExpiryAt = (): number | null => readExpiryAt();

export const isAccessTokenExpired = (): boolean => {
  const expiryAt = readExpiryAt();
  return expiryAt !== null && expiryAt <= Date.now();
};

export const getRemainingAccessTokenMs = (): number => {
  const expiryAt = readExpiryAt();
  if (expiryAt === null) return 0;
  return Math.max(0, expiryAt - Date.now());
};

export const getAccessToken = (): string => {
  try {
    if (typeof window === 'undefined') return '';
    const token = window.localStorage.getItem(ACCESS_TOKEN_KEY) ?? '';
    if (!token) return '';

    const expiryAt = readExpiryAt();
    if (expiryAt === null) {
      window.localStorage.setItem(
        ACCESS_TOKEN_EXPIRES_AT_KEY,
        String(Date.now() + ACCESS_TOKEN_SESSION_TIMEOUT_MS),
      );
      emitTokenStorageChange();
      return token;
    }

    if (expiryAt <= Date.now()) {
      clearStoredToken();
      emitTokenStorageChange();
      return '';
    }

    return token;
  } catch {
    return '';
  }
};

export const setAccessToken = (token?: string): void => {
  try {
    if (typeof window === 'undefined') return;
    if (!token) {
      clearStoredToken();
      emitTokenStorageChange();
      return;
    }
    window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
    window.localStorage.setItem(
      ACCESS_TOKEN_EXPIRES_AT_KEY,
      String(Date.now() + ACCESS_TOKEN_SESSION_TIMEOUT_MS),
    );
    emitTokenStorageChange();
  } catch {
    // ignore storage failures
  }
};

export const clearAccessToken = (): void => {
  setAccessToken('');
};
